'use client'

import type { Token } from '@polaris/core/models'
import { useAtomValue } from 'jotai'
import { CircleHelp } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { Button } from '@/src/components/ui/button'
import { Chevron } from '@/src/components/ui/icons/chevron'
import { ListRowImage } from '@/src/components/ui/list-row'
import { Skeleton } from '@/src/components/ui/skeleton'
import {
  fromTokenAcrossChainsAtom,
  toTokenAcrossChainsAtom,
} from '@/src/stores/swap/atoms'
import { useTokenSelection } from '@/src/stores/token-selection/actions'

interface TokenSelectorProps {
  value?: Token
  direction: 'from' | 'to'
}

/** Includes button and token selection dialog. */
export function TokenSelector({ direction }: TokenSelectorProps) {
  const {
    data,
    isLoading,
    error: tokenAcrossChainsError,
  } = useAtomValue(
    direction === 'from' ? fromTokenAcrossChainsAtom : toTokenAcrossChainsAtom,
  )
  const value = data?.requested_asset
  const { open: openTokenSelection } = useTokenSelection()

  const isNotFoundError = useMemo(() => {
    const cause = tokenAcrossChainsError?.cause

    if (!cause || typeof cause !== 'string') return false

    return (
      cause.includes('not found on chain') || cause.includes('chain not found')
    )
  }, [tokenAcrossChainsError?.cause])

  const internalOpen = useCallback(() => {
    openTokenSelection(direction)
  }, [openTokenSelection, direction])

  return (
    <div className='flex px-3'>
      {isLoading && !value ? (
        <Skeleton className='h-16 w-36 rounded-[64px]' />
      ) : value ? (
        <Button
          type='button'
          size='lg'
          variant='secondary'
          className='rounded-[64px] py-3 pl-3 pr-4 gap-1'
          onClick={internalOpen}
          data-testid='token-select-btn'
        >
          <div className='flex items-center gap-3'>
            <div className='relative w-10'>
              <ListRowImage
                src={value.logo_img_url}
                width={40}
                height={40}
                className='rounded-full max-h-10'
                placeholderName={value.ticker}
                unoptimized
                chainId={value.chain_id}
              />
            </div>
            <h5 className='text-xl font-semibold text-button-secondary-text max-w-[4.5rem] truncate'>
              {value.ticker}
            </h5>
          </div>
        </Button>
      ) : isNotFoundError ? (
        <Button
          type='button'
          size='lg'
          variant='secondary'
          className='rounded-[64px] pl-2 pr-5'
          onClick={() => openTokenSelection(direction)}
          data-testid='token-select-btn'
        >
          <div className='flex items-center justify-center size-8 bg-surface2 rounded-full'>
            <CircleHelp size={24} className='text-tertiary' strokeWidth={2} />
          </div>
          <span className='text-xl font-bold'>—</span>
        </Button>
      ) : (
        <Button
          type='button'
          size='lg'
          variant='outline'
          className='rounded-[64px]'
          onClick={() => openTokenSelection(direction)}
          data-testid='token-select-btn'
        >
          Select token
          <Chevron width={16} height={16} />
        </Button>
      )}
    </div>
  )
}
